// File: frontend/src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { setUnauthorizedCallback } from "../services/apiClient";
import { SESSION_INACTIVITY_TIMEOUT_MINUTES, SESSION_MAX_AGE_HOURS } from "../constants/settings.constants";
import { connectWebSocket, disconnectWebSocket, onSessionRevoked } from "../services/websocketService";
import i18n from "../i18n";

interface User {
  id: number;
  username: string;
  email: string;
  status: string;
  // Permanent role (ROOKIE/AUTHOR/MODERATOR/ADMIN) — never changed by lifecycle operations.
  // Use this for voting restrictions and entry graying of the logged-in user.
  role: string;
  // Resolved avatar URL (full URL with API base), null if no avatar set.
  avatarUrl: string | null;
}

/**
 * Legal consent captured by the registration form.
 *
 * termsVersion is the version string the user was actually shown. Sending it
 * back means that if an admin edits the Terms between page load and submit,
 * the user is not recorded as having accepted the newer text.
 */
export interface RegisterConsent {
  acceptedTerms: boolean;
  confirmedAge: boolean;
  termsVersion?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Issue C: ID of the entry the logged-in user has pinned to their profile (null = none)
  pinnedEntryId: number | null;
  setPinnedEntryId: (id: number | null) => void;
  // Resolves with the outcome of the login so the caller can react to an account
  // state that still allows access but needs to be shown to the user — currently a
  // pending self-deletion, where getting in IS the way to cancel it.
  login: (identifier: string, password: string) => Promise<LoginOutcome>;
  // Authenticate via Google Sign-In using the ID token from Google Identity Services
  loginWithGoogle: (idToken: string) => Promise<LoginOutcome>;
  // Returns whether the backend sent a verification email (true = email verification required)
  register: (
    username: string,
    email: string,
    password: string,
    consent: RegisterConsent
  ) => Promise<{ requiresEmailVerification: boolean }>;

  logout: () => void;
  // Update user status in local state + localStorage without a full re-login
  updateUserStatus: (newStatus: string) => void;
  // Update avatar URL in local state + localStorage (called after upload/delete in ProfilePage)
  updateUserAvatar: (url: string | null) => void;
}

// API Base URL - can be configured for different environments
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

// Resolve a relative media URL to a full URL using the API base.
// Mirrors profileService.resolveMediaUrl so AuthContext stays self-contained.
const resolveMediaUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${API_BASE_URL}${url}`;
};

// API Error type for backend error responses
interface ApiError {
  message?: string;
  error?: string;
  errors?: Record<string, string>;
}

// Login response type from backend
interface LoginResponse {
  id: number;
  username: string;
  email: string;
  status: string;
  role: string;
  token: string;
  // Non-null only when the user has a pending self-deletion request
  scheduledDeletionAt: string | null;
  message: string;
}

/**
 * What the caller of login() gets back.
 *
 * The session is already established by the time this resolves — this is purely so
 * the login form knows whether it should close silently or warn the user first.
 */
export interface LoginOutcome {
  status: string;
  scheduledDeletionAt: string | null;
}

// Register response type from backend
interface RegisterResponse {
  message: string;
}

// Session storage keys
const STORAGE_KEYS = {
  USER: "user",
  TOKEN: "token",
  LOGIN_TIME: "loginTime",
  LAST_ACTIVITY: "lastActivity",
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Issue C: track which entry the logged-in user has pinned globally
  const [pinnedEntryId, setPinnedEntryId] = useState<number | null>(null);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Logout function - memoized to use as callback
  const logout = useCallback((): void => {
    // Disconnect WebSocket before clearing auth state
    disconnectWebSocket();
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.LOGIN_TIME);
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
    setUser(null);
    // Issue C: clear pinned entry on logout
    setPinnedEntryId(null);

    // Clear timers
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
  }, []);

  // Update user status in local state + localStorage without requiring a full re-login.
  // Used after account deletion cancellation to restore the user's active status.
  const updateUserStatus = useCallback((newStatus: string) => {
    setUser((prev) => {
      if (!prev) return prev;
      // Only status changes — role is permanent and only set at login
      const updated = { ...prev, status: newStatus };
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Apply a role change pushed by the server while the user is signed in.
  //
  // The role is stored at login time and never refreshed afterwards, so an admin
  // promoting or demoting somebody mid-session left that copy stale: the header
  // badge, the entry styling and the admin-panel access check all read it. The
  // backend itself was never stale — it resolves the role from the database on
  // every request — so this only brings the browser back in step. No re-login and
  // no new token: the token carries no role used for authorization.
  const applyRoleChange = useCallback((newRole: string) => {
    if (!newRole) return;
    setUser((prev) => {
      if (!prev || prev.role === newRole) return prev;
      const updated = { ...prev, role: newRole };
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Update avatar URL in local state + localStorage after profile avatar upload or delete.
  const updateUserAvatar = useCallback((url: string | null) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, avatarUrl: url };
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Update last activity timestamp
  const updateLastActivity = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
  }, []);

  // Check if session is expired
  const isSessionExpired = useCallback((): { expired: boolean; reason?: string } => {
    const loginTimeStr = localStorage.getItem(STORAGE_KEYS.LOGIN_TIME);
    const lastActivityStr = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);

    if (!loginTimeStr) {
      return { expired: true, reason: "no_login_time" };
    }

    const now = Date.now();
    const loginTime = parseInt(loginTimeStr, 10);
    const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : loginTime;

    // Check max session age (24 hours default)
    const maxAgeMs = SESSION_MAX_AGE_HOURS * 60 * 60 * 1000;
    if (now - loginTime > maxAgeMs) {
      return { expired: true, reason: "max_age_exceeded" };
    }

    // Check inactivity timeout (30 minutes default)
    const inactivityMs = SESSION_INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
    if (now - lastActivity > inactivityMs) {
      return { expired: true, reason: "inactivity_timeout" };
    }

    return { expired: false };
  }, []);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    if (user) {
      updateLastActivity();
    }
  }, [user, updateLastActivity]);

  // Set up activity listeners and session check interval
  useEffect(() => {
    if (!user) return;

    // User activity events to track
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];

    // Throttled activity handler (update at most every 60 seconds)
    let lastUpdate = Date.now();
    const throttledHandler = () => {
      const now = Date.now();
      if (now - lastUpdate > 60000) { // 60 seconds throttle
        lastUpdate = now;
        handleUserActivity();
      }
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, throttledHandler, { passive: true });
    });

    // Initial activity update
    updateLastActivity();

    // Periodic session check (every minute)
    checkIntervalRef.current = setInterval(() => {
      const { expired, reason } = isSessionExpired();
      if (expired) {
        console.log(`Session expired: ${reason}`);
        logout();
      }
    }, 60000); // Check every 60 seconds

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, throttledHandler);
      });
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [user, handleUserActivity, updateLastActivity, isSessionExpired, logout]);

  // Get user info from localStorage when the page loads
  useEffect(() => {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

    if (storedUser && storedToken) {
      // Check if session is expired before restoring
      const { expired, reason } = isSessionExpired();

      if (expired) {
        console.log(`Session expired on load: ${reason}`);
        logout();
      } else {
        try {
          const parsedUser: User = JSON.parse(storedUser);
          // Backward compat: old sessions stored role as status value (ROOKIE/AUTHOR etc.)
          // If role is missing, default to AUTHOR (safe fallback for existing logged-in users)
          if (!parsedUser.role) parsedUser.role = "AUTHOR";
          setUser(parsedUser);
          // Update last activity on page load
          updateLastActivity();
          // Reconnect WebSocket on session restore
          if (storedToken) {
            connectWebSocket(storedToken);
          }
          // Issue C: fetch pinned entry ID for this user in the background
          fetch(`${API_BASE_URL}/api/profile/${parsedUser.id}`, {
            headers: { "Authorization": `Bearer ${storedToken}`, "Content-Type": "application/json" },
          })
            .then((r) => r.ok ? r.json() : null)
            .then((data) => {
              if (data?.pinnedEntry?.id) setPinnedEntryId(data.pinnedEntry.id);
              // Sync latest avatar URL into the stored user object
              const latestAvatarUrl = resolveMediaUrl(data?.avatarUrl ?? null);
              if (latestAvatarUrl !== parsedUser.avatarUrl) {
                const synced = { ...parsedUser, avatarUrl: latestAvatarUrl };
                localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(synced));
                setUser(synced);
              }
            })
            .catch(() => { /* non-critical, ignore */ });
        } catch (error) {
          console.error("Failed to parse stored user:", error);
          logout();
        }
      }
    }
    setIsLoading(false);
  }, [isSessionExpired, logout, updateLastActivity]);

  // Set up the unauthorized callback for apiClient
  useEffect(() => {
    setUnauthorizedCallback(logout);
  }, [logout]);

  // Forced logout pushed from the server. An admin suspending, banning or deleting
  // an account has to end the session that account already has open — the token in
  // localStorage stays valid until it expires, so nothing else would throw the user
  // out while they sit on a page without making requests. The backend also answers
  // 401 on the next request, which covers a tab that was offline when this fired.
  //
  // The same queue also carries ROLE_CHANGED, which must NOT log anyone out: the
  // account is still allowed in, only its role moved. Treating every event on this
  // queue as a logout would turn a promotion into a forced sign-out.
  useEffect(() => {
    if (!user) return;
    return onSessionRevoked((event) => {
      if (event?.event === "ROLE_CHANGED") {
        applyRoleChange(event.role ?? event.reason ?? "");
        return;
      }
      logout();
    });
  }, [user, logout, applyRoleChange]);

  // Shared "successful login" tail: stores the session, updates state, connects
  // the WebSocket, and fetches the pinned entry / avatar. Used by both password
  // login and Google login once the backend has returned a valid LoginResponse.
  const applyLoginResponse = async (data: LoginResponse): Promise<void> => {
    // Create user object
    const loggedInUser: User = {
      id: data.id,
      username: data.username,
      email: data.email,
      status: data.status,
      role: data.role,
      avatarUrl: null, // Will be populated after profile fetch below
    };

    // Save to localStorage with session timestamps
    const now = Date.now().toString();
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(loggedInUser));
    localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
    localStorage.setItem(STORAGE_KEYS.LOGIN_TIME, now);
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now);

    // Update state
    setUser(loggedInUser);

    // Connect WebSocket for real-time messaging
    connectWebSocket(data.token);

    // Issue C: fetch pinned entry ID right after login
    try {
      const profileRes = await fetch(`${API_BASE_URL}/api/profile/${data.id}`, {
        headers: { "Authorization": `Bearer ${data.token}`, "Content-Type": "application/json" },
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setPinnedEntryId(profileData?.pinnedEntry?.id ?? null);
        // Store avatar URL so the header can display it immediately
        const avatarUrl = resolveMediaUrl(profileData?.avatarUrl ?? null);
        const userWithAvatar = { ...loggedInUser, avatarUrl };
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userWithAvatar));
        setUser(userWithAvatar);
      }
    } catch {
      // Non-critical: pinned state will just show as unpinned until next load
    }
  };

  // LOGIN - uses direct fetch since we don't have token yet
  const login = async (identifier: string, password: string): Promise<LoginOutcome> => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ identifier, password }),
    });

    // Handle non-OK responses
    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({}));

      const errorMessage =
        errorData.message ||
        errorData.error ||
        `Login failed with status: ${response.status}`;

      throw new Error(errorMessage);
    }

    // Parse successful response
    const data: LoginResponse = await response.json();
    await applyLoginResponse(data);
    return { status: data.status, scheduledDeletionAt: data.scheduledDeletionAt ?? null };
  };

  // LOGIN WITH GOOGLE - sends the ID token from Google Identity Services to the backend,
  // which verifies it and returns the same LoginResponse shape as regular /login.
  const loginWithGoogle = async (idToken: string): Promise<LoginOutcome> => {
    const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({}));

      const errorMessage =
        errorData.message ||
        errorData.error ||
        `Google login failed with status: ${response.status}`;

      throw new Error(errorMessage);
    }

    const data: LoginResponse = await response.json();
    await applyLoginResponse(data);
    return { status: data.status, scheduledDeletionAt: data.scheduledDeletionAt ?? null };
  };

  // REGISTER - sends Accept-Language header so the backend can send the verification
  // email in the user's current UI language (TR or EN).
  // Returns { requiresEmailVerification: true } when the backend sent a verification email.
  const register = async (
    username: string,
    email: string,
    password: string,
    consent: RegisterConsent
  ): Promise<{ requiresEmailVerification: boolean }> => {
    // Read the active language from i18next (e.g. "tr" or "en")
    const language = i18n.language?.startsWith("tr") ? "tr" : "en";

    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
      // Consent travels with the registration: the backend rejects the request
      // outright when either flag is missing, and records the exact version of
      // the Terms text that was displayed to this user.
      body: JSON.stringify({
        username,
        email,
        password,
        acceptedTerms: consent.acceptedTerms,
        confirmedAge: consent.confirmedAge,
        termsVersion: consent.termsVersion,
      }),
    });

    // Handle non-OK responses
    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({}));

      const errorMessage =
        errorData.message ||
        errorData.error ||
        (errorData.errors ? Object.values(errorData.errors).join(", ") : null) ||
        `Registration failed with status: ${response.status}`;

      throw new Error(errorMessage);
    }

    const data: RegisterResponse = await response.json();
    const requiresEmailVerification = data.message === "EMAIL_VERIFICATION_SENT";

    console.log("User registered successfully:", { username, email, requiresEmailVerification });
    return { requiresEmailVerification };
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    pinnedEntryId,
    setPinnedEntryId,
    login,
    loginWithGoogle,
    register,
    logout,
    updateUserStatus,
    updateUserAvatar,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};